import {isNumber} from 'lodash';
import React, {memo} from 'react';
import {Cell, Row} from 'react-table';
import styled, {css} from 'styled-components';

import {PredicateOf} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {isDefined} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {RandomSkeleton} from '../../../components/common/randomSkeleton';
import {isColumnNumerical, isCustomTableColumn} from '../reportElements/table/tableHelpers';
import {stickyColumn} from './tableCommons';

/*
 * Const.
 */

const CELL_PADDING = 12;

/*
 * Props.
 */

interface TableRowProps<T extends object> {
  prepareRow: (arg: Row<T>) => void;
  row: Row<T>;
  isLoading?: boolean;
  isFirstColumnSticky?: boolean;
  hasOverflow?: boolean;
  hasScrolled?: boolean;
  onCellSelect?: (arg: Cell<T>) => void;
  isCellSelectable?: PredicateOf<Cell<T>>;
  shouldHideBottomBorder: boolean;
}

/*
 * Styles.
 */

const StyledBodyRow = styled.tr`
  background-color: ${(p) => p.theme.foreground};
  &:hover {
    background-color: ${(p) => p.theme.greys.shade10};
  }
`;

const StyledBodyCell = styled.td<{
  $isNumerical: boolean;
  $isSticky: boolean;
  $hasClickListener: boolean;
  $minWidth: number | undefined;
  $maxWidth: number | undefined;
  $width: string | undefined;
  $shouldHideBottomBorder: boolean;
}>`
  ${(p) => isDefined(p.$minWidth) && `min-width: ${p.$minWidth}px;`}
  ${(p) => isDefined(p.$maxWidth) && `max-width: ${p.$maxWidth}px;`}
  ${(p) => isDefined(p.$width) && `width: ${p.$width};`}

  padding: ${CELL_PADDING}px ${CELL_PADDING}px ${CELL_PADDING - 1}px ${CELL_PADDING}px;

  text-align: ${(p) => (p.$isNumerical ? 'right' : 'left')};
  font-weight: ${fontWeights.normal};
  ${(p) =>
    !p.$shouldHideBottomBorder &&
    css`
      border-bottom: 1px solid ${p.theme.greys.shade30};
    `};

  ${(p) => p.$isSticky && stickyColumn}
  ${(p) =>
    p.$hasClickListener &&
    css`
      &:hover {
        color: ${p.theme.interactiveText};
      }
    `}
`;

const StyledCellContent = styled.div`
  cursor: inherit;
  ${ellipsis()};
`;

/*
 * Component.
 */

const TableRowComponent = <T extends Object>({
  prepareRow,
  row,
  isLoading,
  isFirstColumnSticky,
  hasOverflow,
  hasScrolled,
  onCellSelect,
  isCellSelectable,
  shouldHideBottomBorder,
}: TableRowProps<T>) => {
  // Prepare the row for display
  prepareRow(row);

  return (
    // Apply the row props
    <StyledBodyRow {...row.getRowProps()}>
      {
        // Loop over the rows cells
        row.cells.map((cell) => {
          const {column} = cell;
          const isNumerical = isCustomTableColumn(column) ? isColumnNumerical(column.type) : false;
          const isFirstColumn = column.index === 0;
          const isSticky = Boolean(isFirstColumnSticky && isFirstColumn && hasOverflow && hasScrolled);

          // If isCellSelectable is provided, cell is clickable if it returns true, otherwise it's clickable if we have an onCellSelect handler.
          const hasSelectionHandler = typeof onCellSelect === 'function';
          const hasClickListener =
            hasSelectionHandler && isCellSelectable ? isCellSelectable(cell) : hasSelectionHandler;

          const widthAsString = isNumber(column.width) ? `${column.width}px` : column.width;

          return (
            // Apply the cell props
            <StyledBodyCell
              {...cell.getCellProps()}
              $isNumerical={isNumerical}
              $isSticky={isSticky}
              $hasClickListener={hasClickListener}
              $minWidth={column.minWidth}
              $maxWidth={column.maxWidth}
              $width={widthAsString}
              $shouldHideBottomBorder={shouldHideBottomBorder}
              onClick={() => hasClickListener && handleCellClick(cell, onCellSelect)}
            >
              <StyledCellContent>
                {isLoading ? <RandomSkeleton height="10px" /> : cell.render('Cell')}
              </StyledCellContent>
            </StyledBodyCell>
          );
        })
      }
    </StyledBodyRow>
  );
};

export const TableRow = memo(TableRowComponent) as typeof TableRowComponent;

/*
 * Helpers.
 */

function handleCellClick<T extends object>(cell: Cell<T>, onCellSelect?: (arg: Cell<T>) => void) {
  if (!onCellSelect) {
    return;
  }

  onCellSelect(cell);
}
